/* ===== RTL Cart Drawer ===== */
(() => {
  if (window.RtlCart) return;

  const SECTION = 'rtl-cart-drawer';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const drawer = () => document.querySelector('rtl-cart-drawer');

  /* ---------- Helpers ---------- */
  const safeClosest = (el, selector) => {
    if (!selector || !el || !el.closest) return null;
    try { return el.closest(selector); } catch (e) { return null; }
  };

  const readJSON = (el) => {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  const debounce = (fn, wait) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const bubbleSection = () => {
    const d = drawer();
    return d && d.dataset.bubbleSection ? d.dataset.bubbleSection.trim() : '';
  };
  const sectionIds = () => [SECTION, bubbleSection()].filter(Boolean);
  const sectionsPayload = () => ({ sections: sectionIds(), sections_url: window.location.pathname });

  async function cartRequest(endpoint, body) {
    const res = await nativeFetch(`${root}cart/${endpoint}.js`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ ...body, ...sectionsPayload() })
    });
    let data = {};
    try { data = await res.json(); } catch (e) { /* ignore */ }
    if (!res.ok) {
      const err = new Error(data.description || data.message || '');
      err.data = data;
      throw err;
    }
    return data;
  }

  async function fetchSection() {
    const res = await nativeFetch(`${window.location.pathname}?sections=${sectionIds().join(',')}`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /* ---------- Custom element ---------- */
  class RtlCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.termsAccepted = false;
      this.giftSyncing = false;
      this.upsellCache = new Map();
      this.saveNote = debounce((value) => this.updateCart({ note: value }, false), 600);

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));

      this.afterRender();
    }

    get panel() { return this.querySelector('.rcd__panel'); }
    get content() { return this.querySelector('[data-rcd-content]'); }
    get isOpen() { return this.classList.contains('is-open'); }
    get dir() { return this.panel ? this.panel.getAttribute('dir') : 'rtl'; }

    /* ---- Open / close ---- */
    open() {
      if (this.isOpen) return;
      this._lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.documentElement.classList.add('rcd-lock');
      setTimeout(() => {
        if (this.panel) this.panel.focus({ preventScroll: true });
      }, 50);
      document.dispatchEvent(new CustomEvent('rtlcart:open'));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('rcd-lock');
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('rtlcart:close'));
    }

    toggle() { this.isOpen ? this.close() : this.open(); }

    /* ---- Rendering ---- */
    applyBubble(sections) {
      const id = bubbleSection();
      const sel = this.dataset.bubbleSelector;
      if (!id || !sel || !sections || !sections[id]) return;
      try {
        const doc = new DOMParser().parseFromString(sections[id], 'text/html');
        const fresh = doc.querySelector(sel);
        if (!fresh) return;
        document.querySelectorAll(sel).forEach((el) => { if (!this.contains(el)) el.innerHTML = fresh.innerHTML; });
      } catch (e) { /* invalid selector */ }
    }

    async refresh(input) {
      try {
        let sections = input;
        if (!sections) sections = await fetchSection();
        if (typeof sections === 'string') sections = { [SECTION]: sections };
        this.applyBubble(sections);
        const markup = sections && sections[SECTION];
        if (!markup) return;
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const fresh = doc.querySelector('[data-rcd-content]');
        if (!fresh || !this.content) return;

        const scroll = this.querySelector('[data-rcd-scroll]');
        const scrollTop = scroll ? scroll.scrollTop : 0;
        const openDetails = [...this.querySelectorAll('details[data-rcd-keep][open]')].map((d) => d.dataset.rcdKeep);
        const closedDetails = [...this.querySelectorAll('details[data-rcd-keep]:not([open])')].map((d) => d.dataset.rcdKeep);
        const codeInput = this.querySelector('[data-rcd-code-input]');
        const codeValue = codeInput ? codeInput.value : '';

        this.content.innerHTML = fresh.innerHTML;

        const newScroll = this.querySelector('[data-rcd-scroll]');
        if (newScroll) newScroll.scrollTop = scrollTop;
        this.querySelectorAll('details[data-rcd-keep]').forEach((d) => {
          if (openDetails.includes(d.dataset.rcdKeep)) d.open = true;
          if (closedDetails.includes(d.dataset.rcdKeep)) d.open = false;
        });
        const newCode = this.querySelector('[data-rcd-code-input]');
        if (newCode && codeValue) newCode.value = codeValue;

        this.afterRender();
      } catch (e) {
        console.error('[RTL Cart]', e);
      }
    }

    afterRender() {
      this.state = readJSON(this.querySelector('[data-rcd-state]')) || { itemCount: 0, subtotal: 0, gifts: [], discountCodes: [] };
      this.renderProgress();
      this.applyTerms();
      this.updateCountBubbles();
      this.loadAutoUpsell();
      this.syncGifts();
      document.dispatchEvent(new CustomEvent('rtlcart:updated', { detail: { itemCount: this.state.itemCount } }));
    }

    setLoading(on) { this.classList.toggle('is-loading', !!on); }

    showError(message) {
      const box = this.querySelector('[data-rcd-error]');
      if (!box) return;
      box.textContent = message || this.dataset.errorText || 'Error';
      box.hidden = false;
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => { box.hidden = true; }, 6000);
    }

    /* ---- Money ---- */
    formatMoney(cents) {
      const currency = (this.state && this.state.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      const lang = ((this.panel && this.panel.getAttribute('lang')) || document.documentElement.lang || 'ar').split('-')[0];
      const locale = `${lang}-u-nu-${this.dataset.digits === 'arab' ? 'arab' : 'latn'}`;
      const amount = cents / 100;
      const opts = { style: 'currency', currency };
      if (this.dataset.hideDecimals === 'true' && Number.isInteger(amount)) {
        opts.minimumFractionDigits = 0;
        opts.maximumFractionDigits = 0;
      }
      try { return new Intl.NumberFormat(locale, opts).format(amount); }
      catch (e) { return `${amount.toFixed(2)} ${currency}`; }
    }

    /* ---- Progress bar ---- */
    getTiers() {
      const tiers = readJSON(this.querySelector('[data-rcd-tiers]')) || [];
      const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
      return tiers
        .map((t) => ({ ...t, cents: Math.round(Number(t.threshold || 0) * 100 * rate) }))
        .sort((a, b) => a.cents - b.cents);
    }

    renderProgress() {
      const box = this.querySelector('[data-rcd-progress]');
      if (!box) return;
      const tiers = this.getTiers();
      if (!tiers.length) { box.hidden = true; return; }

      const subtotal = this.state.subtotal || 0;
      const max = tiers[tiers.length - 1].cents || 1;
      const pct = Math.max(0, Math.min(100, (subtotal / max) * 100));

      const fill = box.querySelector('[data-rcd-progress-fill]');
      if (fill) requestAnimationFrame(() => { fill.style.width = `${pct}%`; });

      box.querySelectorAll('[data-rcd-step]').forEach((step) => {
        const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
        const cents = Math.round(Number(step.dataset.threshold || 0) * 100 * rate);
        const p = Math.max(0, Math.min(100, (cents / max) * 100));
        step.style.insetInlineStart = `calc(30px + (100% - 60px) * ${p / 100})`;
        step.classList.toggle('is-done', subtotal >= cents);
      });

      const next = tiers.find((t) => subtotal < t.cents);
      const msgEl = box.querySelector('[data-rcd-progress-msg]');
      if (!msgEl) return;
      let text;
      if (next) {
        const remaining = Math.max(0, next.cents - subtotal);
        text = this.escape(next.locked || '').replace(/\[amount\]/g, `<strong>${this.escape(this.formatMoney(remaining))}</strong>`);
      } else {
        text = this.escape(tiers[tiers.length - 1].unlocked || '');
      }
      msgEl.innerHTML = text;
    }

    escape(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    /* ---- Free gift tiers ---- */
    async syncGifts() {
      if (this.giftSyncing) return;
      const tiers = this.getTiers().filter((t) => t.type === 'gift' && t.giftVariantId);
      const gifts = (this.state.gifts || []).filter(Boolean);
      const subtotal = this.state.subtotal || 0;

      const toAdd = [];
      const toRemove = {};

      tiers.forEach((t) => {
        const inCart = gifts.some((g) => String(g.tier) === String(t.id));
        const reached = subtotal >= t.cents && this.state.itemCount > gifts.length;
        if (reached && !inCart) toAdd.push({ id: t.giftVariantId, quantity: 1, properties: { _rcd_gift: t.id } });
      });

      gifts.forEach((g) => {
        const tier = tiers.find((t) => String(t.id) === String(g.tier));
        if (!tier || subtotal < tier.cents) toRemove[g.key] = 0;
      });

      if (!toAdd.length && !Object.keys(toRemove).length) return;

      this.giftSyncing = true;
      try {
        let data = null;
        if (Object.keys(toRemove).length) data = await cartRequest('update', { updates: toRemove });
        if (toAdd.length) data = await cartRequest('add', { items: toAdd });
        if (data && data.sections) await this.refresh(data.sections);
      } catch (e) {
        console.warn('[RTL Cart] gift sync', e);
      } finally {
        this.giftSyncing = false;
      }
    }

    /* ---- Upsell (auto recommendations) ---- */
    async loadAutoUpsell() {
      const track = this.querySelector('[data-rcd-upsell-auto-target]');
      if (!track) return;
      const { productId, limit, intent, cartIds } = track.dataset;
      const key = `${productId}|${intent}|${cartIds}`;
      try {
        let html = this.upsellCache.get(key);
        if (html === undefined) {
          const url = `${root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=${SECTION}`;
          const res = await nativeFetch(url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const auto = doc.querySelector('[data-rcd-upsell-auto]');
          html = auto ? auto.innerHTML.trim() : '';
          this.upsellCache.set(key, html);
        }
        const current = this.querySelector('[data-rcd-upsell-auto-target]');
        if (html && current) {
          current.innerHTML = html;
          const wrap = current.closest('[data-rcd-upsell]');
          if (wrap) wrap.hidden = false;
        }
      } catch (e) { /* keep manual fallback */ }
    }

    /* ---- Terms ---- */
    applyTerms() {
      const terms = this.querySelector('[data-rcd-terms]');
      const btn = this.querySelector('[data-rcd-checkout]');
      if (!btn) return;
      if (!terms) { btn.disabled = false; return; }
      terms.checked = this.termsAccepted;
      btn.disabled = !this.termsAccepted;
    }

    /* ---- Header counters ---- */
    updateCountBubbles() {
      const sel = this.dataset.countSelectors;
      if (!sel) return;
      try {
        document.querySelectorAll(sel).forEach((el) => {
          if (this.contains(el)) return;
          el.textContent = this.state.itemCount;
        });
      } catch (e) { /* invalid selector */ }
    }

    /* ---- Cart actions ---- */
    async changeLine(key, quantity, lineEl) {
      if (lineEl) lineEl.classList.add('is-busy');
      this.setLoading(true);
      try {
        const data = await cartRequest('change', { id: key, quantity });
        await this.refresh(data.sections);
      } catch (e) {
        await this.refresh();
        this.showError(e.message);
      } finally {
        this.setLoading(false);
      }
    }

    async updateCart(body, render = true) {
      if (render) this.setLoading(true);
      try {
        const data = await cartRequest('update', body);
        if (render) await this.refresh(data.sections);
        return data;
      } catch (e) {
        this.showError(e.message);
        return null;
      } finally {
        if (render) this.setLoading(false);
      }
    }

    async addItems(items, button) {
      if (button) button.classList.add('is-busy');
      this.setLoading(true);
      try {
        const data = await cartRequest('add', { items });
        await this.refresh(data.sections);
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.setLoading(false);
        if (button) button.classList.remove('is-busy');
      }
    }

    async applyCode() {
      const input = this.querySelector('[data-rcd-code-input]');
      const msg = this.querySelector('[data-rcd-code-msg]');
      if (!input) return;
      const code = input.value.trim();
      if (!code) return;
      const existing = (this.state.discountCodes || []).map((d) => d.code);
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        if (msg) { msg.textContent = this.dataset.duplicateCodeText; msg.hidden = false; }
        return;
      }
      await this.updateCart({ discount: [...existing, code].join(',') });
      const applied = (this.state.discountCodes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      const newInput = this.querySelector('[data-rcd-code-input]');
      const newMsg = this.querySelector('[data-rcd-code-msg]');
      if (!applied || !applied.applicable) {
        if (newMsg) { newMsg.textContent = this.dataset.invalidCodeText; newMsg.hidden = false; }
      } else if (newInput) {
        newInput.value = '';
      }
    }

    async removeCode(code) {
      const remaining = (this.state.discountCodes || []).map((d) => d.code).filter((c) => c !== code);
      await this.updateCart({ discount: remaining.join(',') });
    }

    slide(direction) {
      const track = this.querySelector('[data-rcd-upsell-track]');
      if (!track) return;
      const card = track.querySelector('.rcd-up');
      const step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth * 0.8;
      const sign = this.dir === 'rtl' ? -1 : 1;
      track.scrollBy({ left: (direction === 'next' ? step : -step) * sign, behavior: 'smooth' });
    }

    /* ---- Events ---- */
    onClick(e) {
      const t = e.target;

      if (t.closest('[data-rcd-close]')) { e.preventDefault(); this.close(); return; }

      const qtyBtn = t.closest('[data-rcd-qty]');
      if (qtyBtn) {
        const line = qtyBtn.closest('[data-rcd-line]');
        const input = line.querySelector('[data-rcd-qty-input]');
        const current = parseInt(input.value, 10) || 0;
        const next = qtyBtn.dataset.rcdQty === 'plus' ? current + 1 : Math.max(0, current - 1);
        input.value = next;
        this.changeLine(line.dataset.key, next, line);
        return;
      }

      const remove = t.closest('[data-rcd-remove]');
      if (remove) {
        const line = remove.closest('[data-rcd-line]');
        this.changeLine(line.dataset.key, 0, line);
        return;
      }

      const upAdd = t.closest('[data-rcd-up-add]');
      if (upAdd) {
        const card = upAdd.closest('[data-rcd-up]');
        const select = card && card.querySelector('[data-rcd-up-variant]');
        const id = select ? select.value : upAdd.dataset.variantId;
        this.addItems([{ id: Number(id), quantity: 1 }], upAdd);
        return;
      }

      if (t.closest('[data-rcd-code-apply]')) { this.applyCode(); return; }

      const rm = t.closest('[data-rcd-code-remove]');
      if (rm) { this.removeCode(rm.dataset.rcdCodeRemove); return; }

      const arrow = t.closest('[data-rcd-slide]');
      if (arrow) { this.slide(arrow.dataset.rcdSlide); }
    }

    onChange(e) {
      const t = e.target;

      if (t.matches('[data-rcd-qty-input]')) {
        const line = t.closest('[data-rcd-line]');
        const qty = Math.max(0, parseInt(t.value, 10) || 0);
        this.changeLine(line.dataset.key, qty, line);
        return;
      }

      if (t.matches('[data-rcd-up-variant]')) {
        const opt = t.selectedOptions[0];
        const card = t.closest('[data-rcd-up]');
        const price = card.querySelector('[data-rcd-up-price]');
        if (opt && price) {
          const compare = opt.dataset.compare ? `<s class="rcd-compare">${opt.dataset.compare}</s>` : '';
          price.innerHTML = `${compare}<span class="rcd-price">${opt.dataset.price}</span>`;
        }
        return;
      }

      if (t.matches('[data-rcd-wrap]')) {
        if (t.checked) {
          this.addItems([{ id: Number(t.dataset.variantId), quantity: 1, properties: { _rcd_wrap: '1' } }]);
        } else if (t.dataset.lineKey) {
          this.changeLine(t.dataset.lineKey, 0);
        }
        return;
      }

      if (t.matches('[data-rcd-terms]')) {
        this.termsAccepted = t.checked;
        this.applyTerms();
      }
    }

    onInput(e) {
      if (e.target.matches('[data-rcd-note]')) this.saveNote(e.target.value);
    }

    onSubmit(e) {
      if (e.target.matches('[data-rcd-checkout-form]')) {
        const terms = this.querySelector('[data-rcd-terms]');
        if (terms && !terms.checked) e.preventDefault();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Enter' && e.target.matches('[data-rcd-code-input]')) { e.preventDefault(); this.applyCode(); return; }
      if (e.key === 'Tab' && this.isOpen) {
        const f = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary')]
          .filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  }

  if (!customElements.get('rtl-cart-drawer')) customElements.define('rtl-cart-drawer', RtlCartDrawer);

  /* ---------- Global: open from cart icon ---------- */
  document.addEventListener('click', (e) => {
    const d = drawer();
    if (!d || d.dataset.isCartPage === 'true') return;
    if (e.target.closest && e.target.closest('[data-rcd-allow]')) return;
    const trigger = safeClosest(e.target, d.dataset.cartSelectors);
    if (!trigger || d.contains(trigger)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    d.open();
  }, true);

  /* ---------- Global: intercept add-to-cart forms ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return;
    const d = drawer();
    if (!d || d.dataset.interceptForms !== 'true' || d.contains(form)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const button = e.submitter || form.querySelector('[type="submit"]');
    if (button) { button.setAttribute('aria-busy', 'true'); button.classList.add('loading'); }

    try {
      const fd = new FormData(form);
      fd.append('sections', sectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      const res = await nativeFetch(`${root}cart/add.js`, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await res.json();
      if (!res.ok) {
        if (d.dataset.openOnAdd === 'true') d.open();
        d.showError(data.description || data.message);
        return;
      }
      await d.refresh(data.sections);
      if (d.dataset.openOnAdd === 'true') d.open();
    } catch (err) {
      d.showError();
    } finally {
      if (button) { button.removeAttribute('aria-busy'); button.classList.remove('loading'); }
    }
  }, true);

  /* ---------- Global: watch theme's own cart requests ---------- */
  let pendingOpen = false;
  const queueRefresh = debounce(async () => {
    const d = drawer();
    if (!d) return;
    await d.refresh();
    if (pendingOpen && d.dataset.openOnAdd === 'true' && d.dataset.isCartPage !== 'true') d.open();
    pendingOpen = false;
  }, 150);

  window.fetch = async function (input, init) {
    const res = await nativeFetch(input, init);
    try {
      const d = drawer();
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (d && d.dataset.watchTheme === 'true' && res.ok && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        if (/\/cart\/add/.test(url)) pendingOpen = true;
        queueRefresh();
      }
    } catch (e) { /* ignore */ }
    return res;
  };

  /* ---------- Redirect the theme's own cart popup to this drawer ---------- */
  const themeCartTag = (() => {
    const d = drawer();
    return d && d.dataset.themeCartElement ? d.dataset.themeCartElement.trim().toLowerCase() : '';
  })();
  if (themeCartTag && /^[a-z][a-z0-9]*-[a-z0-9-]*$/.test(themeCartTag)) {
    customElements.whenDefined(themeCartTag).then(() => {
      const ctor = customElements.get(themeCartTag);
      if (!ctor || !ctor.prototype || ctor.prototype.__rcdPatched) return;
      ctor.prototype.__rcdPatched = true;
      ctor.prototype.open = function () {
        const d = drawer();
        if (d && d.dataset.isCartPage !== 'true') d.open();
      };
    });
  }

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === SECTION) { const d = drawer(); if (d) d.open(); } });
  document.addEventListener('shopify:block:select', (e) => { if (e.detail.sectionId === SECTION) { const d = drawer(); if (d) d.open(); } });
  document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === SECTION) { const d = drawer(); if (d) d.close(); } });
  document.addEventListener('shopify:section:load', (e) => {
    if (e.detail.sectionId === SECTION) {
      document.documentElement.classList.remove('rcd-lock');
      setTimeout(() => { const d = drawer(); if (d) d.open(); }, 50);
    }
  });

  /* ---------- Public API ---------- */
  window.RtlCart = {
    open: () => { const d = drawer(); if (d) d.open(); },
    close: () => { const d = drawer(); if (d) d.close(); },
    toggle: () => { const d = drawer(); if (d) d.toggle(); },
    refresh: () => { const d = drawer(); return d ? d.refresh() : null; }
  };
})();
